import { useEffect, useMemo, useState, useCallback } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, fmtDateTime } from "../lib/api";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const ACTION_COLORS = {
  DELETE_BLOCKED: "bg-red-100 text-red-700",
  DELETE_PRODUCT: "bg-red-100 text-red-700",
  CREATE_SALE: "bg-emerald-100 text-emerald-700",
  CLOCK_IN: "bg-blue-100 text-blue-700",
  CLOCK_OUT: "bg-blue-100 text-blue-700",
};

export default function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/audit");
      setLogs(data);
    } catch (e) { toast.error(fmtErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => logs.filter((l) => {
    const q = search.trim().toLowerCase();
    return !q || l.action.toLowerCase().includes(q) || (l.user_name || "").toLowerCase().includes(q) || l.entity.toLowerCase().includes(q);
  }), [logs, search]);

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Audit Trail</h1>
          <p className="text-sm text-slate-500">Immutable compliance ledger · {logs.length} entries</p>
        </div>
        <div className="flex-1" />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter action, user, entity…" className="pl-9 w-72 bg-white" data-testid="audit-search-input" />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Table data-testid="admin-audit-log-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Timestamp</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>Changes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((l) => (
              <TableRow key={l.log_id}>
                <TableCell className="font-mono text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(l.timestamp)}</TableCell>
                <TableCell className="text-sm text-slate-900">{l.user_name || "—"}</TableCell>
                <TableCell>
                  <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${ACTION_COLORS[l.action] || "bg-slate-100 text-slate-700"}`}>{l.action}</span>
                </TableCell>
                <TableCell className="text-xs text-slate-500">{l.entity}</TableCell>
                <TableCell className="font-mono text-[11px] text-slate-500 max-w-xs truncate">
                  {l.new_values ? JSON.stringify(l.new_values) : l.old_values ? JSON.stringify(l.old_values) : "—"}
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-sm text-slate-400">No audit entries.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
